import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { REASON_MAX } from '../../../../../Consts/subscription-control.const';
import {
  SubscriptionConfirmDialogData,
  SubscriptionConfirmDialogResult,
} from '../../../Interfaces/subscription-dialog.interface';

@Component({
  selector: 'app-subscription-confirm-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatDialogModule, MatIconModule, TranslateModule],
  templateUrl: './subscription-confirm-dialog.component.html',
  styleUrl: './subscription-confirm-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubscriptionConfirmDialogComponent {
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _dialogRef =
    inject<MatDialogRef<SubscriptionConfirmDialogComponent, SubscriptionConfirmDialogResult>>(
      MatDialogRef,
    );

  readonly data = inject<SubscriptionConfirmDialogData>(MAT_DIALOG_DATA);

  readonly reasonMax = REASON_MAX;

  readonly hasModes = this.data.modes.length > 0;

  readonly selectedMode = signal<string>(this.data.modes[0] ?? '');

  readonly wantsOverride = signal<boolean>(false);

  readonly modeConsequence = computed<string>(
    () => this.data.modeConsequences[this.selectedMode()] ?? '',
  );

  readonly formGroup: FormGroup = this._formBuilder.group({
    mode: [this.data.modes[0] ?? ''],
    reason: ['', [Validators.required, Validators.maxLength(REASON_MAX)]],
    override_commitment_lock: [false],
    override_reason: ['', [Validators.maxLength(REASON_MAX)]],
  });

  readonly isSubmittable = computed<boolean>(
    () => !this.wantsOverride() || this._overrideReasonFilled(),
  );

  constructor() {
    this._registerModeSync();
    this._registerOverrideSync();
  }

  confirm(): void {
    if (!this.formGroup.valid || !this.isSubmittable()) {
      return;
    }

    this._dialogRef.close({
      reason: String(this.formGroup.value.reason).trim(),
      mode: this.hasModes ? String(this.formGroup.value.mode) : null,
      overrideCommitmentLock: Boolean(this.formGroup.value.override_commitment_lock),
      overrideReason: this.wantsOverride()
        ? String(this.formGroup.value.override_reason).trim()
        : null,
    });
  }

  dismiss(): void {
    this._dialogRef.close();
  }

  private _registerModeSync(): void {
    this.formGroup.controls['mode'].valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this.selectedMode.set(String(value ?? '')));
  }

  private _registerOverrideSync(): void {
    this.formGroup.controls['override_commitment_lock'].valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this.wantsOverride.set(Boolean(value)));
  }

  private _overrideReasonFilled(): boolean {
    return String(this.formGroup.value.override_reason ?? '').trim().length > 0;
  }
}
